import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Spotlight + explanation card for one tour step.
// The highlighted element is found by data-tour="<target>"; the rest of the page is dimmed and not clickable.

const PAD = 8;
const CARD_W = 360;

export default function TourOverlay({ step, index, total, onNext, onBack, onSkip, onMissing }) {
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(!step.target);
  const [vw, setVw] = useState(window.innerWidth);
  const nextBtn = useRef(null);
  const isLast = index === total - 1;

  // Wait for the target to appear (pages load data first), then scroll it into view
  useEffect(() => {
    if (!step.target) return undefined;
    let tries = 0;
    let cancelled = false;
    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      const visible = el && el.getBoundingClientRect().height > 0;
      if (visible) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(() => { if (!cancelled) setReady(true); }, 350);
      } else if (++tries > 40) { // ~4 seconds
        if (step.optional) onMissing();
        else setReady(true); // show as a centred card instead
      } else {
        setTimeout(find, 100);
      }
    };
    find();
    return () => { cancelled = true; };
  }, [step, onMissing]);

  // Track the element's position as the page scrolls or resizes
  useLayoutEffect(() => {
    if (!ready || !step.target) return undefined;
    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
      setVw(window.innerWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const t = setInterval(measure, 500); // layout can shift as data loads
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      clearInterval(t);
    };
  }, [ready, step.target]);

  // Keyboard: → / Enter next, ← back, Esc to leave
  useEffect(() => {
    if (!ready) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft' && index > 0) { e.preventDefault(); onBack(); }
      else if (e.key === 'Escape') { e.preventDefault(); onSkip(); }
    };
    window.addEventListener('keydown', onKey);
    nextBtn.current?.focus({ preventScroll: true });
    return () => window.removeEventListener('keydown', onKey);
  }, [ready, index, onNext, onBack, onSkip]);

  if (!ready) {
    // Dim the page while we wait, so nothing can be clicked mid-transition
    return <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(2,6,23,0.55)' }} />;
  }

  const mobile = vw < 640;
  const spot = rect && {
    top: rect.top - PAD, left: rect.left - PAD,
    width: rect.width + PAD * 2, height: rect.height + PAD * 2,
  };

  // Card position: below the target if there's room, else above; centred when there is no target
  let cardStyle;
  if (!spot || mobile) {
    cardStyle = mobile && spot
      ? { left: 16, right: 16, bottom: 16 }
      : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: `min(${CARD_W + 60}px, calc(100vw - 32px))` };
  } else {
    const below = window.innerHeight - (spot.top + spot.height) > 230;
    const left = Math.min(Math.max(16, spot.left), vw - CARD_W - 16);
    cardStyle = below
      ? { top: spot.top + spot.height + 12, left, width: CARD_W }
      : { top: Math.max(16, spot.top - 12), left, width: CARD_W, transform: 'translateY(-100%)' };
  }

  return (
    <>
      {/* Click shield */}
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: spot ? 'transparent' : 'rgba(2,6,23,0.72)' }} />

      {/* Spotlight: a transparent hole with a huge shadow dimming everything else */}
      {spot && (
        <div aria-hidden="true" style={{
          position: 'fixed', zIndex: 1001, pointerEvents: 'none', borderRadius: 12,
          top: spot.top, left: spot.left, width: spot.width, height: spot.height,
          boxShadow: '0 0 0 9999px rgba(2,6,23,0.72), 0 0 0 2px #38bdf8',
          transition: 'all 0.3s ease',
        }} />
      )}

      {/* Card */}
      <div role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-body"
        style={{
          position: 'fixed', zIndex: 1002, ...cardStyle,
          background: '#0f172a', border: '1px solid rgba(56,189,248,0.35)', borderRadius: 14,
          boxShadow: '0 20px 50px rgba(0,0,0,0.6)', padding: 20, boxSizing: 'border-box',
          animation: 'tourIn 0.25s ease',
        }}>
        <style>{'@keyframes tourIn { from { opacity: 0; } to { opacity: 1; } }'}</style>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ color: '#64748b', fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
            Step {index + 1} of {total}
          </span>
          <button onClick={onSkip} aria-label="Close tour"
            style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 16, cursor: 'pointer', padding: 2 }}>✕</button>
        </div>

        <h3 id="tour-title" style={{ color: '#e2e8f0', fontSize: 17, fontWeight: 700, margin: '0 0 8px' }}>{step.title}</h3>
        <p id="tour-body" style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.55, margin: '0 0 16px' }}>{step.body}</p>

        {/* Progress */}
        <div style={{ height: 4, background: 'rgba(148,163,184,0.15)', borderRadius: 2, marginBottom: 16 }}>
          <div style={{ width: `${((index + 1) / total) * 100}%`, height: '100%', background: '#38bdf8', borderRadius: 2, transition: 'width 0.3s ease' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isLast && (
            <button onClick={onSkip} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', padding: 0 }}>
              Skip tour
            </button>
          )}
          <div style={{ flex: 1 }} />
          {index > 0 && (
            <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 14px', color: '#94a3b8', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Back
            </button>
          )}
          <button ref={nextBtn} onClick={onNext}
            style={{ background: 'linear-gradient(135deg, #0891b2, #0e7490)', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {isLast ? 'Finish' : index === 0 ? "Let's go" : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
}
